import {
  computed,
  onUnmounted,
  ref,
  watch,
  type ComputedRef,
  type Ref,
} from 'vue';

/**
 * Green top progress strip in DashboardLayout (`.dashboard-loadbar`).
 * Multiple sources can request the bar; it stays on until all release.
 */
const sources = new Set<string>();
let sourceSeq = 0;

/** Reactive flag bound by DashboardLayout (avoids fragile DOM queries). */
export const dashboardLoadbarActive = ref(false);

function syncLoadbar() {
  dashboardLoadbarActive.value = sources.size > 0;
}

/** Toggle one named source (e.g. `route`, `referrer-dashboard`). */
export function setDashboardLoadbarSource(source: string, enabled: boolean) {
  const key = source.trim() || 'default';
  if (enabled) sources.add(key);
  else sources.delete(key);
  syncLoadbar();
}

/** Legacy helper — uses the shared `manual` source. */
export function setDashboardLoadbar(enabled: boolean) {
  setDashboardLoadbarSource('manual', enabled);
}

/** Keep a source in sync with a busy flag; clears on unmount. */
export function useDashboardLoadbar(
  busy: Ref<boolean> | ComputedRef<boolean>,
  sourceId?: string,
) {
  const id = sourceId?.trim() || `local-${++sourceSeq}`;
  watch(
    busy,
    (on) => setDashboardLoadbarSource(id, on),
    { immediate: true },
  );
  onUnmounted(() => setDashboardLoadbarSource(id, false));
}

/** True when any loadbar source is active (for tests / debug). */
export const dashboardLoadbarSourceCount = computed(() => sources.size);
